import {
  answerKeys,
  getDraftAnswer,
  isMeaningfulAnswer,
  type DraftAnswerMap,
  type DraftAnswerValue,
} from './answer-model'
import type { RunnerQuestionDefinition, RunnerSectionDefinition } from './model'

export type NumberedAnswer = { number: string | number; answer: string }
export type TransformedAnswer =
  | string
  | readonly string[]
  | Record<string, string>
  | readonly NumberedAnswer[]

export class AnswerEncodingError extends Error {
  constructor(message: string, public readonly questionId: string) {
    super(message)
    this.name = 'AnswerEncodingError'
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function clean(value: DraftAnswerValue | undefined): DraftAnswerValue | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || undefined
  }
  if (Array.isArray(value)) {
    const values = value.map((item) => item.trim()).filter(Boolean)
    return values.length ? values : undefined
  }
  return undefined
}

function stableItemId(item: Record<string, unknown>, questionId: string, kind: string): string {
  const candidate = item.id ?? item[`${kind}_id`]
  if (typeof candidate !== 'string' && typeof candidate !== 'number') {
    throw new AnswerEncodingError(`${kind} requires a stable id; array indexes and question numbers are not valid identity`, questionId)
  }
  const id = String(candidate).trim()
  if (!id) throw new AnswerEncodingError(`${kind} requires a non-empty stable id`, questionId)
  return id
}

function scalarAnswer(question: RunnerQuestionDefinition, answers: DraftAnswerMap): TransformedAnswer | null {
  return clean(getDraftAnswer(answers, answerKeys.question(question.id))) ?? null
}

function blankIds(question: RunnerQuestionDefinition): string[] {
  const data = question.typeData ?? {}
  const configured = Array.isArray(data.blanks) ? data.blanks : []
  if (configured.length) {
    return configured.map((entry) => {
      if (typeof entry === 'string' || typeof entry === 'number') return String(entry)
      return stableItemId(asRecord(entry), question.id, 'blank')
    })
  }
  const ids = [...(question.text ?? '').matchAll(/\{\{([^{}]+)\}\}/g)].map((match) => match[1].trim())
  return [...new Set(ids.filter(Boolean))]
}

function fillInBlankAnswer(question: RunnerQuestionDefinition, answers: DraftAnswerMap): TransformedAnswer | null {
  const ids = blankIds(question)
  if (ids.length === 0) return scalarAnswer(question, answers)

  const output: Record<string, string> = {}
  for (const blankId of ids) {
    const value = clean(getDraftAnswer(answers, answerKeys.blank(question.id, blankId)))
    if (typeof value === 'string') output[`blank_${blankId}`] = value
  }
  return Object.keys(output).length ? output : null
}

function matchingHeadingsAnswer(question: RunnerQuestionDefinition, answers: DraftAnswerMap): TransformedAnswer | null {
  const paragraphs = Array.isArray(question.typeData?.paragraphs) ? question.typeData.paragraphs : []
  const output: Record<string, string> = {}
  for (const raw of paragraphs) {
    const item = asRecord(raw)
    const itemId = typeof raw === 'string' || typeof raw === 'number'
      ? String(raw)
      : stableItemId(item, question.id, 'paragraph')
    const submissionKey = String(item.key ?? item.label ?? itemId)
    const value = clean(getDraftAnswer(answers, answerKeys.paragraph(question.id, itemId)))
    if (typeof value === 'string') output[submissionKey] = value
  }
  return Object.keys(output).length ? output : null
}

function matchingInformationAnswer(question: RunnerQuestionDefinition, answers: DraftAnswerMap): TransformedAnswer | null {
  const statements = asRecord(question.typeData?.statements)
  const output: Record<string, string> = {}
  for (const statementId of Object.keys(statements)) {
    const value = clean(getDraftAnswer(answers, answerKeys.statement(question.id, statementId)))
    if (typeof value === 'string') output[statementId] = value
  }
  return Object.keys(output).length ? JSON.stringify(output) : null
}

function numberedItemsAnswer(
  question: RunnerQuestionDefinition,
  answers: DraftAnswerMap,
  collection: 'sentences' | 'questions',
): TransformedAnswer | null {
  const items = Array.isArray(question.typeData?.[collection]) ? question.typeData[collection] : []
  const output: NumberedAnswer[] = []
  for (const raw of items) {
    const item = asRecord(raw)
    const kind = collection === 'sentences' ? 'sentence' : 'picture_item'
    const itemId = stableItemId(item, question.id, kind)
    const submissionNumber = item.number
    if (typeof submissionNumber !== 'string' && typeof submissionNumber !== 'number') {
      throw new AnswerEncodingError(`${kind} ${itemId} is missing its backend submission number`, question.id)
    }
    const key = collection === 'sentences'
      ? answerKeys.sentence(question.id, itemId)
      : answerKeys.pictureItem(question.id, itemId)
    const value = clean(getDraftAnswer(answers, key))
    if (typeof value === 'string') output.push({ number: submissionNumber, answer: value })
  }
  return output.length ? output : null
}

function speakingAnswer(question: RunnerQuestionDefinition, answers: DraftAnswerMap): TransformedAnswer | null {
  const media = clean(getDraftAnswer(answers, answerKeys.media(question.id)))
  const legacyMedia = clean(getDraftAnswer(answers, answerKeys.question(question.id)))
  const mediaId = typeof media === 'string'
    ? media
    : typeof legacyMedia === 'string' && legacyMedia !== 'recorded'
      ? legacyMedia
      : undefined
  const noteValue = clean(getDraftAnswer(answers, answerKeys.notes(question.id)))
  const notes = question.type === 'long_turn' && typeof noteValue === 'string' ? noteValue : undefined
  if (!mediaId && !notes) return null
  return { ...(mediaId ? { media_id: mediaId } : {}), ...(notes ? { notes } : {}) }
}

export function encodeQuestionAnswer(
  question: RunnerQuestionDefinition,
  answers: DraftAnswerMap,
): TransformedAnswer | null {
  switch (question.type) {
    case 'fill_in_blank':
      return fillInBlankAnswer(question, answers)
    case 'short_answer':
      return fillInBlankAnswer(question, answers)
    case 'matching_headings':
      return matchingHeadingsAnswer(question, answers)
    case 'matching_information':
      return matchingInformationAnswer(question, answers)
    case 'sentence_completion':
      return numberedItemsAnswer(question, answers, 'sentences')
    case 'questions_with_picture':
      return numberedItemsAnswer(question, answers, 'questions')
    case 'speaking':
    case 'long_turn':
    case 'interview_style':
      return speakingAnswer(question, answers)
    default:
      return scalarAnswer(question, answers)
  }
}

export function encodeSectionAnswers(
  section: RunnerSectionDefinition,
  answers: DraftAnswerMap,
): Readonly<Record<string, TransformedAnswer>> {
  const output: Record<string, TransformedAnswer> = {}
  for (const part of section.parts) {
    for (const question of part.questions) {
      const value = encodeQuestionAnswer(question, answers)
      if (value !== null && isMeaningfulTransformedAnswer(value)) output[question.id] = value
    }
  }
  return output
}

function isMeaningfulTransformedAnswer(value: TransformedAnswer): boolean {
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return Object.keys(value).length > 0
}

/* -------------------------------------------------------------------------- */
/* Decoding: the server's encoded answers back into the runner's answer map.   */
/* -------------------------------------------------------------------------- */

/**
 * The inverse of `encodeQuestionAnswer`, for DRAFT RESUME only (grading robustness, owner rule 5). The server stores a draft
 * in the same shape `submit_section` takes (`draft_answers`), so giving the student their typed answers back means turning that
 * shape into the runner's own keys again. It reads the question's own authored ids from the definition, exactly as the encoder
 * does, so the two cannot drift: `encode(decode(encode(x)))` equals `encode(x)` for every type here (the codec tests prove it).
 *
 * It is deliberately forgiving: an entry whose shape or ids it does not recognise is left out (that one answer is not
 * restored), and it never throws. A Speaking answer is an upload, not a draft, and is never decoded.
 */
function decodeScalar(question: RunnerQuestionDefinition, value: unknown): DraftAnswerMap {
  if (typeof value === 'string') return { [answerKeys.question(question.id)]: value }
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) return { [answerKeys.question(question.id)]: value as readonly string[] }
  return {}
}

function decodeBlanks(question: RunnerQuestionDefinition, value: unknown): DraftAnswerMap {
  const ids = blankIds(question)
  if (ids.length === 0) return decodeScalar(question, value)
  const record = asRecord(value)
  const out: Record<string, DraftAnswerValue> = {}
  for (const blankId of ids) {
    const entry = record[`blank_${blankId}`]
    if (typeof entry === 'string') out[answerKeys.blank(question.id, blankId)] = entry
  }
  return out
}

function decodeHeadings(question: RunnerQuestionDefinition, value: unknown): DraftAnswerMap {
  const record = asRecord(value)
  const paragraphs = Array.isArray(question.typeData?.paragraphs) ? question.typeData.paragraphs : []
  const out: Record<string, DraftAnswerValue> = {}
  for (const raw of paragraphs) {
    const item = asRecord(raw)
    const itemId = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : stableItemId(item, question.id, 'paragraph')
    const entry = record[String(item.key ?? item.label ?? itemId)]
    if (typeof entry === 'string') out[answerKeys.paragraph(question.id, itemId)] = entry
  }
  return out
}

function decodeStatements(question: RunnerQuestionDefinition, value: unknown): DraftAnswerMap {
  let record: Record<string, unknown> = {}
  if (typeof value === 'string') {
    try { record = asRecord(JSON.parse(value)) } catch { return {} }
  } else {
    record = asRecord(value)
  }
  const out: Record<string, DraftAnswerValue> = {}
  for (const statementId of Object.keys(asRecord(question.typeData?.statements))) {
    const entry = record[statementId]
    if (typeof entry === 'string') out[answerKeys.statement(question.id, statementId)] = entry
  }
  return out
}

function decodeNumbered(question: RunnerQuestionDefinition, value: unknown, collection: 'sentences' | 'questions'): DraftAnswerMap {
  if (!Array.isArray(value)) return {}
  const answers = new Map<string, string>()
  for (const entry of value) {
    const record = asRecord(entry)
    if ((typeof record.number === 'string' || typeof record.number === 'number') && typeof record.answer === 'string') {
      answers.set(String(record.number), record.answer)
    }
  }
  const items = Array.isArray(question.typeData?.[collection]) ? question.typeData[collection] : []
  const kind = collection === 'sentences' ? 'sentence' : 'picture_item'
  const out: Record<string, DraftAnswerValue> = {}
  for (const raw of items) {
    const item = asRecord(raw)
    if (typeof item.number !== 'string' && typeof item.number !== 'number') continue
    const answer = answers.get(String(item.number))
    if (answer === undefined) continue
    const itemId = stableItemId(item, question.id, kind)
    out[collection === 'sentences' ? answerKeys.sentence(question.id, itemId) : answerKeys.pictureItem(question.id, itemId)] = answer
  }
  return out
}

export function decodeQuestionAnswer(question: RunnerQuestionDefinition, value: unknown): DraftAnswerMap {
  try {
    switch (question.type) {
      case 'fill_in_blank':
      case 'short_answer':
        return decodeBlanks(question, value)
      case 'matching_headings':
        return decodeHeadings(question, value)
      case 'matching_information':
        return decodeStatements(question, value)
      case 'sentence_completion':
        return decodeNumbered(question, value, 'sentences')
      case 'questions_with_picture':
        return decodeNumbered(question, value, 'questions')
      case 'speaking':
      case 'long_turn':
      case 'interview_style':
        return {}
      default:
        return decodeScalar(question, value)
    }
  } catch {
    // An id the authored definition cannot name: that one answer is not restored, the rest are.
    return {}
  }
}

export function decodeSectionAnswers(section: RunnerSectionDefinition, encoded: Readonly<Record<string, unknown>>): DraftAnswerMap {
  const out: Record<string, DraftAnswerValue> = {}
  for (const part of section.parts) {
    for (const question of part.questions) {
      if (!Object.hasOwn(encoded, question.id)) continue
      Object.assign(out, decodeQuestionAnswer(question, encoded[question.id]))
    }
  }
  return out as DraftAnswerMap
}

export function hasQuestionAnswer(question: RunnerQuestionDefinition, answers: DraftAnswerMap): boolean {
  const encoded = encodeQuestionAnswer(question, answers)
  return encoded !== null && isMeaningfulTransformedAnswer(encoded)
}

// Kept exported for consumers that need the same empty-value semantics without encoding.
export { isMeaningfulAnswer }
